import { useCallback, useMemo, useState } from 'react';
import type React from 'react';

interface QuickFieldInserterProps {
  textareaRef: React.RefObject<HTMLTextAreaElement | HTMLInputElement>;
  onInsert: (field: string) => void;
  customFields?: string[];
  disabled?: boolean;
}

type QuickFieldCategory = {
  name: string;
  fields: readonly string[];
};

const ALERT_FIELDS = [
  'alertName',
  'alertId',
  'severity',
  'alertType',
  'alertStatus',
] as const;

const SOURCE_FIELDS = ['sourceName', 'sourceType', 'sourceId'] as const;

const RESOURCE_FIELDS = [
  'resourceId',
  'resourceName',
  'resourceType',
  'resourceGroup',
  'subscriptionId',
] as const;

const DETAIL_FIELDS = [
  'description',
  'summary',
  'additionalDetails',
] as const;

const TIMESTAMP_FIELDS = ['firedAt', 'resolvedAt', 'receivedAt'] as const;

const BUILT_IN_CATEGORIES: readonly QuickFieldCategory[] = [
  { name: 'Alert', fields: ALERT_FIELDS },
  { name: 'Source', fields: SOURCE_FIELDS },
  { name: 'Resource', fields: RESOURCE_FIELDS },
  { name: 'Details', fields: DETAIL_FIELDS },
  { name: 'Timestamps', fields: TIMESTAMP_FIELDS },
] as const;

export const QUICK_FIELD_INSERTER_BUILT_IN_FIELDS = [
  ...ALERT_FIELDS,
  ...SOURCE_FIELDS,
  ...RESOURCE_FIELDS,
  ...DETAIL_FIELDS,
  ...TIMESTAMP_FIELDS,
] as const;

export const QUICK_FIELD_INSERTER_BUILT_IN_FIELD_SET = new Set<string>(
  QUICK_FIELD_INSERTER_BUILT_IN_FIELDS,
);

export function QuickFieldInserter({
  textareaRef,
  onInsert,
  customFields = [],
  disabled = false,
}: QuickFieldInserterProps) {
  const [query, setQuery] = useState('');

  const customCategoryFields = useMemo(() => {
    const uniqueFields = new Set<string>();
    for (const field of customFields) {
      const value = field.trim();
      if (!value || QUICK_FIELD_INSERTER_BUILT_IN_FIELD_SET.has(value)) {
        continue;
      }
      uniqueFields.add(value);
    }
    return Array.from(uniqueFields);
  }, [customFields]);

  const categories = useMemo<QuickFieldCategory[]>(() => {
    if (customCategoryFields.length === 0) {
      return [...BUILT_IN_CATEGORIES];
    }

    return [
      ...BUILT_IN_CATEGORIES,
      { name: 'Custom', fields: customCategoryFields },
    ];
  }, [customCategoryFields]);

  const normalizedQuery = query.trim().toLowerCase();

  const filteredCategories = useMemo(() => {
    if (!normalizedQuery) {
      return categories;
    }

    return categories
      .map((category) => {
        const categoryMatch = category.name.toLowerCase().includes(normalizedQuery);
        if (categoryMatch) {
          return category;
        }

        const filteredFields = category.fields.filter((field) => (
          field.toLowerCase().includes(normalizedQuery)
        ));

        return {
          ...category,
          fields: filteredFields,
        };
      })
      .filter((category) => category.fields.length > 0);
  }, [categories, normalizedQuery]);

  return (
    <div className="mt-2 space-y-2">
      <input
        type="search"
        className="input text-xs"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Filter fields by name or category"
        disabled={disabled}
        aria-label="Filter template fields"
      />

      <div className="space-y-1.5">
        {filteredCategories.map((category) => (
          <details
            key={category.name}
            open={category.name !== 'Custom'}
            className="rounded border border-gray-200 px-2 py-1 dark:border-gray-700"
          >
            <summary className="cursor-pointer select-none text-xs font-medium text-gray-600 dark:text-gray-300">
              {category.name} ({category.fields.length})
            </summary>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {category.fields.map((field) => (
                <button
                  key={`${category.name}-${field}`}
                  type="button"
                  title={`{{${field}}}`}
                  onClick={() => {
                    textareaRef.current?.focus();
                    onInsert(field);
                  }}
                  disabled={disabled}
                  className="inline-flex items-center rounded-full border border-gray-300 bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-700 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                >
                  {field}
                </button>
              ))}
            </div>
          </details>
        ))}

        {filteredCategories.length === 0 && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            No matching fields.
          </p>
        )}
      </div>
    </div>
  );
}

interface AvailableFieldsPaneProps {
  customFields?: string[];
  disabled?: boolean;
}

export function AvailableFieldsPane({
  customFields = [],
  disabled = false,
}: AvailableFieldsPaneProps) {
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const customCategoryFields = useMemo(() => {
    const uniqueFields = new Set<string>();
    for (const field of customFields) {
      const value = field.trim();
      if (!value || QUICK_FIELD_INSERTER_BUILT_IN_FIELD_SET.has(value)) {
        continue;
      }
      uniqueFields.add(value);
    }
    return Array.from(uniqueFields);
  }, [customFields]);

  const categories = useMemo<QuickFieldCategory[]>(() => {
    if (customCategoryFields.length === 0) {
      return [...BUILT_IN_CATEGORIES];
    }

    return [
      ...BUILT_IN_CATEGORIES,
      { name: 'Custom', fields: customCategoryFields },
    ];
  }, [customCategoryFields]);

  const handleDragStart = useCallback((event: React.DragEvent, field: string) => {
    event.dataTransfer.setData('text/plain', `{{${field}}}`);
    event.dataTransfer.effectAllowed = 'copy';
  }, []);

  const handleClick = useCallback((field: string) => {
    const token = `{{${field}}}`;
    navigator.clipboard.writeText(token).catch(() => {});
    setCopiedField(field);
    setTimeout(() => setCopiedField((current) => (current === field ? null : current)), 1500);
  }, []);

  return (
    <div className="space-y-1.5">
      {categories.map((category) => (
        <details
          key={category.name}
          open
          className="rounded border border-gray-200 px-2 py-1 dark:border-gray-700"
        >
          <summary className="cursor-pointer select-none text-xs font-medium text-gray-600 dark:text-gray-300">
            {category.name} ({category.fields.length})
          </summary>
          <div className="mt-1.5 flex flex-wrap gap-1.5 pb-1">
            {category.fields.map((field) => (
              <button
                key={`${category.name}-${field}`}
                type="button"
                draggable={!disabled}
                onDragStart={(event) => handleDragStart(event, field)}
                onClick={() => handleClick(field)}
                disabled={disabled}
                title={`Drag or click to copy {{${field}}}`}
                className="inline-flex items-center rounded-full border border-gray-300 bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-700 transition-colors hover:bg-gray-200 cursor-grab active:cursor-grabbing disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
              >
                {copiedField === field ? '✓ Copied' : field}
              </button>
            ))}
          </div>
        </details>
      ))}
    </div>
  );
}
